import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import CSSTransition from 'react-transition-group/CSSTransition';
import { Button, LinkButton, TypographyV2 as Typography } from '@which/seatbelt';
import { TransitionalPlusMinusIcon } from '@which/seatbelt/src/components/Icons/TransitionalPlusMinus';
import { ControlledTextField } from '../../components/ControlledTextField';
import { OnwardJourney } from '../../components/OnwardJourney';
import { Results } from '../../components/Results';
import { ToolBody } from '../../components/ToolBody';
import { ToolFrame } from '../../components/ToolFrame';
import { ToolHeading } from '../../components/ToolHeading';
import { useTrackedButton, useTrackedInput, useTracking } from '../../hooks';
import { currencyFormatter } from '../../shared/utils';
import styles from './AnnualFuelCalculator.module.scss';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const AnnualFuelCalculator = _ref => {
  let {
    onwardJourneys
  } = _ref;
  const [isExpanded, setIsExpanded] = useState(false);
  const [carOneAnnualCost, setCarOneAnnualCost] = useState(0);
  const [carTwoAnnualCost, setCarTwoAnnualCost] = useState(0);
  const resultsRef = useRef(null);
  const {
    control,
    handleSubmit
  } = useForm({
    defaultValues,
    mode: 'onBlur'
  });
  const trackingState = useTracking({
    name: 'Annual fuel cost calculator',
    category: 'Cars'
  });
  const trackedInputProps = useTrackedInput({
    trackingState
  });
  const trackedExpandButtonProps = useTrackedButton({
    trackingState,
    trackingName: isExpanded ? 'collapseSecondCar' : 'expandSecondCar',
    onClick: () => setIsExpanded(!isExpanded)
  });
  const trackedSubmitButtonProps = useTrackedButton({
    trackingState,
    trackingName: 'calculate'
  });
  useEffect(() => {
    if (carOneAnnualCost) {
      resultsRef.current?.focus();
    }
  }, [carOneAnnualCost]);
  const validation = {
    required: true,
    pattern: /^\-?[0-9]+(?:\.[0-9]{1,2})?$/
  };
  const onSubmit = data => {
    const {
      carOneFuelCost,
      carOneMilesPerGallon,
      carOnePredictedMileage,
      carTwoFuelCost,
      carTwoMilesPerGallon,
      carTwoPredictedMileage
    } = data;

    // Cost is reset to ensure the useEffect runs on every form submission
    setCarOneAnnualCost(0);
    setCarOneAnnualCost(annualFuelCost(carOneFuelCost, carOneMilesPerGallon, carOnePredictedMileage));
    setCarTwoAnnualCost(annualFuelCost(carTwoFuelCost, carTwoMilesPerGallon, carTwoPredictedMileage));
  };
  const fuelCostDifference = carOneAnnualCost - carTwoAnnualCost;
  const resultMessage = fuelCostDifference < 0 ? /*#__PURE__*/_jsxs(_Fragment, {
    children: ["The first car provides the better value for money when it comes to fuel consumption, it is", ' ', currencyFormatter(-fuelCostDifference), " cheaper per year to run."]
  }) : fuelCostDifference > 0 ? /*#__PURE__*/_jsxs(_Fragment, {
    children: ["The second car provides the better value for money when it comes to fuel consumption, it is", ' ', currencyFormatter(fuelCostDifference), " cheaper per year to run."]
  }) : /*#__PURE__*/_jsx(_Fragment, {
    children: "Neither car provides better value for money when it comes to fuel consumption, neither is cheaper per year to run."
  });
  return /*#__PURE__*/_jsxs(ToolFrame, {
    children: [/*#__PURE__*/_jsxs(ToolBody, {
      "aria-labelledby": "annual-fuel-cost-calculator-heading",
      children: [/*#__PURE__*/_jsx(ToolHeading, {
        id: "annual-fuel-cost-calculator-heading",
        children: "Annual fuel cost calculator"
      }), /*#__PURE__*/_jsx(Typography, {
        textStyle: "sb-text-body-default-regular",
        children: "Enter the fuel consumption details below to compare one or two cars to find out their respective running costs. Please enter whole numbers only."
      }), /*#__PURE__*/_jsxs("form", {
        name: "Annual fuel cost calculator",
        onSubmit: handleSubmit(onSubmit),
        children: [/*#__PURE__*/_jsxs("fieldset", {
          className: styles.formFieldSet,
          children: [/*#__PURE__*/_jsx("legend", {
            children: /*#__PURE__*/_jsx(Typography, {
              textStyle: "sb-text-body-default-strong",
              children: "Car 1"
            })
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: "Please enter a valid fuel cost (pence per litre) for car 1",
            inputMode: "decimal",
            label: "Fuel cost (pence per litre)",
            name: "carOneFuelCost",
            validation: validation,
            ...trackedInputProps
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: "Please enter a valid miles per gallon for car 1",
            inputMode: "decimal",
            label: "Miles per gallon",
            name: "carOneMilesPerGallon",
            validation: validation,
            ...trackedInputProps
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: "Please enter a valid predicted annual mileage for car 1",
            inputMode: "decimal",
            label: "Predicted annual mileage",
            name: "carOnePredictedMileage",
            validation: validation,
            ...trackedInputProps
          })]
        }), /*#__PURE__*/_jsx(LinkButton, {
          "aria-controls": "field-set-car-two",
          "aria-expanded": isExpanded ? true : false,
          className: styles.formLinkButton,
          icon: () => /*#__PURE__*/_jsx(TransitionalPlusMinusIcon, {
            "aria-hidden": true,
            "aria-label": undefined,
            height: 16,
            toggle: isExpanded,
            width: 16
          }),
          ...trackedExpandButtonProps,
          children: isExpanded ? 'Remove second car from comparison' : 'Add another car for comparison'
        }), isExpanded && /*#__PURE__*/_jsxs("fieldset", {
          className: styles.formFieldSet,
          id: "field-set-car-two",
          children: [/*#__PURE__*/_jsx("legend", {
            children: /*#__PURE__*/_jsx(Typography, {
              textStyle: "sb-text-body-default-strong",
              children: "Car 2"
            })
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: "Please enter a valid fuel cost (pence per litre) for car 2",
            inputMode: "decimal",
            label: "Fuel cost (pence per litre)",
            name: "carTwoFuelCost",
            validation: validation,
            ...trackedInputProps
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: "Please enter a valid miles per gallon for car 2",
            inputMode: "decimal",
            label: "Miles per gallon",
            name: "carTwoMilesPerGallon",
            validation: validation,
            ...trackedInputProps
          }), /*#__PURE__*/_jsx(ControlledTextField, {
            className: styles.formControlledTextField,
            control: control,
            errorMsg: "Please enter a valid predicted annual mileage for car 2",
            inputMode: "decimal",
            label: "Predicted annual mileage",
            name: "carTwoPredictedMileage",
            validation: validation,
            ...trackedInputProps
          })]
        }), /*#__PURE__*/_jsx(Button, {
          type: "submit",
          ...trackedSubmitButtonProps,
          children: "Calculate costs"
        })]
      })]
    }), /*#__PURE__*/_jsx(CSSTransition, {
      classNames: "results-wrapper",
      in: carOneAnnualCost > 0,
      timeout: 500,
      children: /*#__PURE__*/_jsx(_Fragment, {
        children: carOneAnnualCost > 0 && /*#__PURE__*/_jsxs("div", {
          className: styles.results,
          children: [/*#__PURE__*/_jsxs(Results, {
            "aria-label": "Annual fuel cost calculator results",
            ref: resultsRef,
            tabIndex: -1,
            children: [/*#__PURE__*/_jsx(Typography, {
              className: styles.resultsHeader,
              tag: "h3",
              textStyle: "sb-text-body-default-strong",
              children: "Your results:"
            }), /*#__PURE__*/_jsxs(Typography, {
              textStyle: "sb-text-heading-medium",
              children: ["Annual fuel costs for the ", isExpanded ? '1st' : '', " car:", ' ', currencyFormatter(carOneAnnualCost)]
            }), isExpanded && carTwoAnnualCost > 0 && /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsxs(Typography, {
                className: styles.resultsSecondCar,
                textStyle: "sb-text-heading-medium",
                children: ["Annual fuel costs for the 2nd car: ", currencyFormatter(carTwoAnnualCost)]
              }), /*#__PURE__*/_jsx(Typography, {
                className: styles.resetMargin,
                textStyle: "sb-text-body-default-regular",
                children: resultMessage
              })]
            })]
          }), /*#__PURE__*/_jsx(OnwardJourney, {
            "aria-label": "Annual fuel cost calculator related links",
            itemGroup: "Cars",
            onwardJourneys: onwardJourneys,
            toolName: "Annual fuel cost calculator"
          })]
        })
      })
    })]
  });
};
export default AnnualFuelCalculator;

///////// IMPLEMENTATION /////////

const defaultValues = {
  carOneFuelCost: '',
  carOneMilesPerGallon: '',
  carOnePredictedMileage: '',
  carTwoFuelCost: '',
  carTwoMilesPerGallon: '',
  carTwoPredictedMileage: ''
};
const multiplier = 4.54609; // Number of litres in a gallon

const annualFuelCost = (fuelCost, milesPerGallon, predictedMileage) => {
  const costPerGallon = Number(fuelCost) * multiplier;
  const gallonsNeeded = Number(predictedMileage) / Number(milesPerGallon);
  const totalCost = gallonsNeeded * costPerGallon / 100;
  return totalCost;
};